import p2pFieldLabels, { labelForP2PField } from "../data/p2pFieldLabels";
import { formatAppDateTime } from "./appTimeFormat";

const FINISHED_CODES = new Set([
  "FT",
  "AET",
  "PEN",
  "FT_PEN",
  "FINISHED",
  "CLOSED",
  "ENDED",
]);

export const TICKET_STATUSES = [
  { id: "pending", label: "Pending" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
  { id: "voided", label: "Voided" },
  { id: "paid", label: "Paid" },
  { id: "refunded", label: "Refunded" },
];

export const JOINED_BET_FILTERS = [
  { id: "all", label: "All" },
  ...TICKET_STATUSES,
];

const LIVE_TICKET_STATUSES = new Set(["pending", "active"]);

const SETTLED_TICKET_STATUSES = new Set([
  "won",
  "lost",
  "voided",
  "paid",
  "refunded",
]);

export const normalizeTicketStatus = (status) =>
  String(status || "pending").toLowerCase();

export const formatTicketStatusLabel = (status) => {
  const key = normalizeTicketStatus(status);
  const labels = {
    pending: "Pending",
    active: "Live",
    won: "Won",
    lost: "Lost",
    voided: "Voided",
    paid: "Paid",
    refunded: "Refunded",
  };
  return labels[key] || key.charAt(0).toUpperCase() + key.slice(1);
};

export const isTicketEditable = (status) =>
  LIVE_TICKET_STATUSES.has(normalizeTicketStatus(status));

export const unwrapBettingsList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

export const unwrapBettingDoc = (payload) =>
  payload && typeof payload === "object" && Object.prototype.hasOwnProperty.call(payload, "betting")
    ? payload.betting
    : payload;

export const formatPeriod = (sub) =>
  labelForP2PField(sub) || p2pFieldLabels[sub] || sub || "—";

export const marketTitle = (section) =>
  labelForP2PField(section) || p2pFieldLabels[section] || section || "Market";

export const formatSelection = (section, key) => {
  if (!key) return "—";
  const raw = String(key);
  if (section === "oneXTwo") {
    if (raw === "1") return "Home (1)";
    if (raw === "X") return "Draw (X)";
    if (raw === "2") return "Away (2)";
  }
  if (section === "doubleChance") {
    if (raw === "1X") return "1X";
    if (raw === "12") return "12";
    if (raw === "X2") return "X2";
  }
  if (section === "oddEven") {
    if (raw.toLowerCase() === "odd") return "Odd";
    if (raw.toLowerCase() === "even") return "Even";
  }
  if (section === "overUnderGoals") {
    const match = raw.match(/(over|under)(\d+)_?(\d?)/i);
    if (match) {
      const side = match[1].toLowerCase() === "over" ? "Over" : "Under";
      const whole = match[2];
      const frac = match[3] ? `.${match[3]}` : "";
      return `${side} ${whole}${frac}`;
    }
  }
  return p2pFieldLabels[raw] || raw;
};

export const shortTeam = (name) => {
  if (!name || typeof name !== "string") return "—";
  const t = name.trim();
  if (t.length <= 14) return t;
  return `${t.slice(0, 12)}…`;
};

export const formatFixtureStatus = (fx) => {
  if (!fx) return "—";
  const sh = fx.status?.short;
  if (sh != null && String(sh).trim() !== "") return String(sh).toUpperCase();
  return "—";
};

export const isFixtureFinished = (fixtureItem) => {
  const fx = fixtureItem?.fixture;
  const fixtureStatus =
    fx?.status?.short || fixtureItem?.status?.short || fixtureItem?.status || "";
  return FINISHED_CODES.has(String(fixtureStatus).toUpperCase());
};

export const getTicketProgress = (fixtures = []) => {
  const list = Array.isArray(fixtures) ? fixtures : [];
  const finished = list.filter(isFixtureFinished).length;
  const total = list.length;
  return {
    finished,
    total,
    ratio: total ? finished / total : 0,
  };
};

export const getBetHouseName = (bet) =>
  bet?.bet?.name || bet?.betName || bet?.name || "Bet house";

export const getStatusTone = (status) => {
  const key = normalizeTicketStatus(status);
  switch (key) {
    case "active":
      return "active";
    case "pending":
      return "pending";
    case "won":
      return "won";
    case "lost":
      return "lost";
    case "voided":
      return "voided";
    case "paid":
      return "paid";
    case "refunded":
      return "refunded";
    default:
      return "default";
  }
};

export const filterJoinedBets = (bets, filterId) => {
  if (!Array.isArray(bets)) return [];
  if (filterId === "all") return bets;
  return bets.filter(
    (bet) => normalizeTicketStatus(bet?.status) === normalizeTicketStatus(filterId)
  );
};

export const countJoinedBetsByFilter = (bets, filterId) => {
  if (!Array.isArray(bets)) return 0;
  if (filterId === "all") return bets.length;
  return bets.filter(
    (bet) => normalizeTicketStatus(bet?.status) === normalizeTicketStatus(filterId)
  ).length;
};

export const countTicketsByStatus = (bets) => {
  const counts = { all: Array.isArray(bets) ? bets.length : 0 };
  for (const { id } of TICKET_STATUSES) {
    counts[id] = countJoinedBetsByFilter(bets, id);
  }
  return counts;
};

export const isLiveTicket = (status) =>
  LIVE_TICKET_STATUSES.has(normalizeTicketStatus(status));

export const isSettledTicket = (status) =>
  SETTLED_TICKET_STATUSES.has(normalizeTicketStatus(status));

export const sumStaked = (bets) =>
  (Array.isArray(bets) ? bets : []).reduce(
    (sum, bet) => sum + Number(bet?.amountBet || 0),
    0
  );

export const sumPotential = (bets) =>
  (Array.isArray(bets) ? bets : []).reduce(
    (sum, bet) => sum + Number(bet?.potentialWinnings || 0),
    0
  );

export const getTicketOddsMultiplier = (bet) => {
  const totalOdds = Number(bet?.totalOddsValue);
  if (Number.isFinite(totalOdds) && totalOdds > 0) return totalOdds;

  const stake = Number(bet?.amountBet || 0);
  const potential = Number(bet?.potentialWinnings || 0);
  if (stake > 0 && potential > 0) return potential / stake;

  const legs = Array.isArray(bet?.fixtures) ? bet.fixtures : [];
  if (!legs.length) return 1;
  return legs.reduce(
    (acc, leg) => acc * Number(leg?.selectedOdd?.value || leg?.value || 1),
    1
  );
};

export const projectStakeEdit = (bet, additionalAmount = 0) => {
  const currentStake = Number(bet?.amountBet || 0);
  const currentPotential = Number(bet?.potentialWinnings || 0);
  const additional = Number(additionalAmount);
  const safeAdditional =
    Number.isFinite(additional) && additional > 0 ? additional : 0;
  const multiplier = getTicketOddsMultiplier(bet);
  const newStake = currentStake + safeAdditional;
  const newPotential = newStake * multiplier;

  return {
    currentStake,
    currentPotential,
    additional: safeAdditional,
    newStake,
    newPotential,
    multiplier,
    hasAdditional: safeAdditional > 0,
  };
};

export const formatTicketDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
};

export const formatKickoff = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppDateTime(d);
};

export const teamInitials = (name) => {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase();
};
